import { useCallback, useState } from 'react';
import type { VideoProgress } from '@/types/required-video';

// Per-key loading flag for the initial server/local progress fetch. The
// player and caption read this through context; until it flips false they
// stay in their loading state, so the iframe never mounts with stale
// last_position=0 (seek-on-ready would miss) and the caption never flashes
// the full required duration before the resume value lands.
//
// progressLoading is DERIVED from `loadedKey !== videoKey` rather than a
// useEffect-driven boolean. A flag would lag one commit behind: when
// videoKey changes from FALLBACK ('__loading__') to e.g. 'rules-1', the
// useState would still hold the previous fetch's `false`, and the next
// render would briefly compute combined loading = false, mount the
// player, and render the caption against stale state — that single
// commit was enough for users to see "Faltan 33:01" instead of the
// resume value. Comparing keys synchronously eliminates that gap and
// also stops the player iframe from briefly mounting + unmounting +
// remounting across the videoKey transition.
type LoadGuard = {
  progressLoading: boolean;
  onLoaded: (p: VideoProgress) => void;
};

export function useLoadGuard(
  videoKey: string,
  setLocal: (p: VideoProgress) => void
): LoadGuard {
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const onLoaded = useCallback(
    (p: VideoProgress): void => {
      setLocal(p);
      setLoadedKey(p.video_key);
    },
    [setLocal]
  );
  return { progressLoading: loadedKey !== videoKey, onLoaded };
}
